import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import {
  Add as AddIcon,
  Archive as ArchiveIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as DuplicateIcon,
  People as PeopleIcon,
  BarChart as BarChartIcon,
  Email as EmailIcon,
  School as SchoolIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { getAdminCourseName } from '../utils/courseDisplay';

interface Course {
  id: string;
  courseProject?: string;
  title: string | { [key: string]: string };
  description: string | { [key: string]: string };
  defaultLanguage?: string;
  availableLanguages?: string[];
  tags?: string[];
  isActive: boolean;
  coach: {
    id: string;
    name: string;
  };
  enrolledStudents: number;
  totalChapters: number;
  createdAt: string;
}

const COURSE_TAG_OPTIONS = [
  'ELearning',
  'Blended',
  'Coaching',
  'Template',
  'Autre'
] as const;

const Courses: React.FC = () => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [tagFilters, setTagFilters] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [enrollStudentDialogOpen, setEnrollStudentDialogOpen] = useState(false);
  const [studentEmail, setStudentEmail] = useState('');
  const [groupOptions, setGroupOptions] = useState<Array<{ id: string; name: string }>>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [success, setSuccess] = useState('');
  
  const { user } = useAuth();
  const navigate = useNavigate();

  const filteredCourses = useMemo(() => {
    if (tagFilters.length === 0) return courses;
    return courses.filter((c) => {
      const tags = c.tags || [];
      return tagFilters.some((f) => tags.includes(f));
    });
  }, [courses, tagFilters]);

  const toggleTagFilter = (tag: string) => {
    setTagFilters((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  useEffect(() => {
    fetchCourses();
  }, []);

  useEffect(() => {
    if (enrollStudentDialogOpen && selectedCourse?.id) {
      const token = localStorage.getItem('admin_token');
      axios
        .get(`${process.env.REACT_APP_API_URL}/groups/course/${selectedCourse.id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'ngrok-skip-browser-warning': '1'
          }
        })
        .then((res) => {
          const groups = res.data || [];
          setGroupOptions(groups);
          // Pré-sélectionner le premier groupe si disponible
          if (groups.length > 0) {
            setSelectedGroupId(groups[0].id);
          }
        })
        .catch(() => setGroupOptions([]));
    } else {
      setGroupOptions([]);
      setSelectedGroupId('');
    }
  }, [enrollStudentDialogOpen, selectedCourse?.id]);

  const fetchCourses = async () => {
    let debugUrl = '';
    try {
      const token = localStorage.getItem('admin_token');
      const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
      debugUrl = `${apiBase}/courses`;
      console.log('[Courses] fetchCourses url:', debugUrl, 'tokenPresent:', Boolean(token));
      const response = await axios.get(debugUrl, {
        headers: token
          ? { Authorization: `Bearer ${token}`, 'ngrok-skip-browser-warning': '1' }
          : { 'ngrok-skip-browser-warning': '1' }
      });
      setCourses(response.data);
      setSuccess('');
      setLoading(false);
    } catch (error: any) {
      const status = error?.response?.status;
      const apiError = error?.response?.data?.error || error?.response?.data?.message;
      console.error('[Courses] fetchCourses failed:', {
        status,
        apiError,
        message: error?.message,
        url: debugUrl
      });
      setError(
        apiError
          ? `Erreur de chargement des formations (${status}) : ${apiError}`
          : `Erreur de chargement des formations (${status || 'sans-statut'})`
      );
      setLoading(false);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, course: Course) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedCourse(course);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEditCourse = () => {
    if (selectedCourse) {
      navigate(`/courses/${selectedCourse.id}/edit`);
    }
    handleMenuClose();
  };

  const handleDeleteCourse = () => {
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const confirmDeleteCourse = async () => {
    if (!selectedCourse) return;
    const courseToDelete = selectedCourse;
    try {
      const token = localStorage.getItem('admin_token');
      const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
      await axios.delete(`${apiBase}/courses/${courseToDelete.id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'ngrok-skip-browser-warning': '1',
        },
      });
      setCourses((prev) => prev.filter((c) => c.id !== courseToDelete.id));
      setDeleteDialogOpen(false);
      setSelectedCourse(null);
      setSuccess('Formation supprimée avec succès');
      setError('');
    } catch (error: any) {
      setDeleteDialogOpen(false);
      setError(
        error.response?.data?.error ||
          error.response?.data?.message ||
          'Erreur lors de la suppression de la formation'
      );
    }
  };

  const handleEnrollStudent = async () => {
    if (!selectedCourse || !studentEmail || !selectedGroupId) return;

    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/courses/${selectedCourse.id}/enroll`,
        {
          studentEmail,
          groupId: selectedGroupId
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      setEnrollStudentDialogOpen(false);
      setStudentEmail('');
      setSelectedCourse(null);
      setSelectedGroupId('');
    } catch (error: any) {
      setError(error.response?.data?.error || 'Erreur lors de l’inscription de l’apprenant');
    }
  };

  const handleDuplicateCourse = async (course: Course) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/courses/${course.id}/duplicate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess('Formation dupliquée avec succès');
      await fetchCourses();
    } catch (error: any) {
      setError(error.response?.data?.error || 'Erreur lors de la duplication de la formation');
    }
  };

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h4" gutterBottom fontWeight="bold">
            Gestion des formations
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {user?.role === 'admin' ? 'Gérer tous les cours du système' : 'Gérer vos cours assignés'}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/courses/new')}
          size="large"
        >
          Nouvelle formation
        </Button>
      </Box>

      {/* Statistiques rapides */}
      {courses.length > 0 && (
        <Box display="flex" gap={2} mb={3}>
          <Card sx={{ p: 2, minWidth: 120 }}>
            <Typography variant="h6" color="primary">
              {courses.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Formation{courses.length > 1 ? 's' : ''} au total
            </Typography>
          </Card>
          <Card sx={{ p: 2, minWidth: 120 }}>
            <Typography variant="h6" color="success.main">
              {courses.filter(c => c.isActive).length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Actif{courses.filter(c => c.isActive).length > 1 ? 's' : ''}
            </Typography>
          </Card>
          <Card sx={{ p: 2, minWidth: 120 }}>
            <Typography variant="h6" color="info.main">
              {courses.reduce((sum, c) => sum + c.enrolledStudents, 0)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Apprenants inscrits
            </Typography>
          </Card>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      {courses.length === 0 ? (
        <Card sx={{ textAlign: 'center', py: 6 }}>
          <CardContent>
            <SchoolIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Aucune formation trouvée
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {user?.role === 'admin' 
                ? 'Créez votre première formation pour commencer' 
                : 'Aucune formation ne vous a encore été assignée'}
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} sx={{ mt: 2 }} onClick={() => navigate('/courses/new')}>
              Créer une formation
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <Box
            sx={{
              mb: 2,
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 1,
              rowGap: 1.5,
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>
              Filtrer par tags :
            </Typography>
            {COURSE_TAG_OPTIONS.map((tag) => {
              const selected = tagFilters.includes(tag);
              return (
                <Chip
                  key={tag}
                  label={tag}
                  onClick={() => toggleTagFilter(tag)}
                  color={selected ? 'primary' : 'default'}
                  variant={selected ? 'filled' : 'outlined'}
                  size="small"
                  sx={{ fontWeight: selected ? 600 : 400 }}
                />
              );
            })}
            {tagFilters.length > 0 && (
              <Button size="small" onClick={() => setTagFilters([])}>
                Effacer les filtres
              </Button>
            )}
          </Box>

          {filteredCourses.length === 0 ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              Aucune formation ne correspond aux tags sélectionnés. Modifiez ou réinitialisez le filtre.
            </Alert>
          ) : (
        <TableContainer 
          component={Paper} 
          sx={{ 
            mt: 2,
            maxHeight: 600,
            overflow: 'auto',
            '&::-webkit-scrollbar': {
              width: '8px',
              height: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(0,0,0,0.1)',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.3)',
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.5)',
              },
            },
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell><strong>Titre</strong></TableCell>
                <TableCell><strong>Apprenants</strong></TableCell>
                <TableCell><strong>Sections</strong></TableCell>
                <TableCell><strong>Langues</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCourses.map((course) => (
                <TableRow key={course.id} hover>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight="600">
                      {getAdminCourseName(course)}
                    </Typography>
                    {course.tags && course.tags.length > 0 && (
                      <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.75}>
                        {course.tags.map((tag) => (
                          <Chip
                            key={tag}
                            label={tag}
                            size="small"
                            variant="outlined"
                            color="primary"
                            sx={{
                              height: 22,
                              fontSize: '0.7rem',
                              '& .MuiChip-label': { px: 1 }
                            }}
                          />
                        ))}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <PeopleIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        {course.enrolledStudents}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {course.totalChapters}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5}>
                      {course.availableLanguages?.map((lang) => (
                        <Chip
                          key={lang}
                          label={lang.toUpperCase()}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem', height: 20 }}
                        />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      <Tooltip title="Modifier">
                        <IconButton 
                          size="small" 
                          onClick={() => navigate(`/courses/${course.id}/edit`)}
                          color="primary"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Statistiques">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/courses/${course.id}/analytics`)}
                          color="success"
                        >
                          <BarChartIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Gérer les apprenants">
                        <IconButton 
                          size="small" 
                          onClick={() => navigate(`/courses/${course.id}/students`)}
                          color="secondary"
                        >
                          <PeopleIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Relances automatiques">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/courses/${course.id}/reminders`)}
                          color="info"
                        >
                          <EmailIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Export SCORM">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/courses/${course.id}/scorm`)}
                          color="warning"
                        >
                          <ArchiveIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Dupliquer la formation">
                        <IconButton
                          size="small"
                          onClick={() => handleDuplicateCourse(course)}
                          color="info"
                        >
                          <DuplicateIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Plus d'options">
                        <IconButton 
                          size="small" 
                          onClick={(e) => handleMenuOpen(e, course)}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
          )}
        </>
      )}

      {/* Menu de la formation */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEditCourse}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Modifier la formation
        </MenuItem>
        <MenuItem onClick={() => {
          setEnrollStudentDialogOpen(true);
          handleMenuClose();
        }}>
          <PeopleIcon fontSize="small" sx={{ mr: 1 }} />
          Inscrire un apprenant
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedCourse) {
            navigate(`/courses/${selectedCourse.id}/reminders`);
          }
          handleMenuClose();
        }}>
          <EmailIcon fontSize="small" sx={{ mr: 1 }} />
          Relances automatiques
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedCourse) {
            navigate(`/courses/${selectedCourse.id}/scorm`);
          }
          handleMenuClose();
        }}>
          <ArchiveIcon fontSize="small" sx={{ mr: 1 }} />
          Export SCORM
        </MenuItem>
        {user?.role === 'admin' && (
          <MenuItem onClick={handleDeleteCourse} sx={{ color: 'error.main' }}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            Supprimer la formation
          </MenuItem>
        )}
      </Menu>

      {/* Dialogue de confirmation de suppression */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Supprimer la formation</DialogTitle>
        <DialogContent>
          <Typography>
            Confirmez-vous la suppression de "{selectedCourse ? getAdminCourseName(selectedCourse) : ''}" ? Cette action est irréversible.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Annuler</Button>
          <Button onClick={confirmDeleteCourse} color="error" variant="contained">
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialogue d'inscription d'un apprenant */}
      <Dialog open={enrollStudentDialogOpen} onClose={() => setEnrollStudentDialogOpen(false)}>
        <DialogTitle>Inscrire un apprenant</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Email de l'apprenant"
            type="email"
            fullWidth
            variant="outlined"
            value={studentEmail}
            onChange={(e) => setStudentEmail(e.target.value)}
          />
          {selectedCourse && (
            <FormControl fullWidth margin="dense" size="small">
              <InputLabel>Groupe</InputLabel>
              <Select
                label="Groupe"
                value={selectedGroupId}
                onChange={(e) => setSelectedGroupId(e.target.value)}
              >
                {groupOptions.map((group) => (
                  <MenuItem key={group.id} value={group.id}>
                    {group.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEnrollStudentDialogOpen(false)}>Annuler</Button>
          <Button onClick={handleEnrollStudent} variant="contained">
            Inscrire
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Courses; 